import { api } from "../../api";
import { h } from "../shared/safe";
import { pill } from "../shared/blocks";
import { SOURCE_KINDS, SOURCE_KIND_LABELS, UNRESOLVED_FIELDS, describeProblem, fieldLabel, parseUnresolvedList, previewPromotion, readUnresolved, showValue, type Problem, type UnresolvedItem } from "./factsModel";
import { busyButton, card, labelled, notice, problemNotice, select, textArea, textInput, whoText } from "./kit";
import { enc, type Workspace } from "./workspace";

// UNRESOLVED INFORMATION: things someone said, found or inferred that nobody has confirmed. It is stored apart from the approved facts and
// is never used for content or sites. It is drawn differently everywhere (dashed amber frame, "NOT APPROVED" label, "Proposed (unverified)")
// so it cannot be mistaken for an approved fact. The only way in is Promote, which shows EXACTLY what will become approved, then asks.

export function unresolvedCard(ws: Workspace): HTMLElement {
  const body = h("div");
  const out = card("fx-unresolved", "Unresolved information", body);
  out.classList.add("fx-unresolved");
  let items: UnresolvedItem[] | null = null;
  let loadError: string | null = null;
  let showClosed = false;
  let flash: HTMLElement | null = null;
  /** The item whose promotion is being confirmed, or whose rejection note is being typed. */
  let promoting: { id: string; stale: Problem | null; error: Problem | null } | null = null;
  let rejecting: string | null = null;
  const form = h("form", { class: "fx-form fx-form--unresolved", novalidate: true, "aria-label": "Record unresolved information" });
  let draft = { field: "phone", label: "", value: "", sourceKind: "client", sourceRef: "", note: "" };

  async function load() {
    try {
      const list = parseUnresolvedList(await api<unknown>(`/v1/clients/${enc(ws.clientId)}/unresolved`));
      if (!ws.life.alive()) return;
      items = list;
      loadError = list ? null : "The server sent a list this page does not understand.";
    } catch (e) {
      if (!ws.life.alive()) return;
      loadError = describeProblem(e, "Unresolved information could not be loaded").message;
    }
    paint();
  }

  function buildForm() {
    const fieldSel = select(UNRESOLVED_FIELDS.map((f): [string, string] => [f, f === "other" ? "Other (a note; cannot be promoted)" : fieldLabel(f)]), draft.field, { name: "field", onChange: (v) => { draft.field = v; sync(); } });
    const label = textInput(draft.label, { name: "label", onInput: (v) => void (draft.label = v) });
    const value = textArea(draft.value, { name: "value", rows: 2, onInput: (v) => void (draft.value = v) });
    const source = select(SOURCE_KINDS.map((k): [string, string] => [k, SOURCE_KIND_LABELS[k] ?? k]), draft.sourceKind, { name: "sourceKind", onChange: (v) => void (draft.sourceKind = v) });
    const ref = textInput(draft.sourceRef, { name: "sourceRef", onInput: (v) => void (draft.sourceRef = v) });
    const note = textInput(draft.note, { name: "note", onInput: (v) => void (draft.note = v) });
    const labelField = labelled("What is it? (required for Other)", label, { help: 'For example "Second location?"' });
    const sync = () => void (labelField.hidden = draft.field !== "other");
    sync();
    const add = busyButton({ label: "Record as unresolved", busyLabel: "Recording…", id: "fx-unr-add", type: "submit", onClick: () => addItem() });
    form.onsubmit = (e) => {
      e.preventDefault();
      void add.click();
    };
    form.replaceChildren(
      h("div", { class: "fx-grid" }, labelled("Which fact is it about?", fieldSel), labelField, labelled("Proposed value (unverified)", value, { wide: true }), labelled("Where did it come from?", source), labelled("Source details (optional)", ref), labelled("Note (optional)", note, { wide: true })),
      h("div", { class: "btn-group" }, add),
      h("div", { id: "fx-unr-form-result", "aria-live": "polite" }),
    );
  }

  async function addItem() {
    const result = form.querySelector("#fx-unr-form-result")!;
    if (draft.value.trim() === "") return void result.replaceChildren(notice("bad", "Enter the proposed value first. Nothing was recorded."));
    if (draft.field === "other" && draft.label.trim() === "") return void result.replaceChildren(notice("bad", 'A note ("Other") needs a short label. Nothing was recorded.'));
    try {
      const res = await api<{ item?: unknown }>(`/v1/clients/${enc(ws.clientId)}/unresolved`, {
        method: "POST",
        body: JSON.stringify({ field: draft.field, value: draft.value.trim(), ...(draft.field === "other" ? { label: draft.label.trim() } : {}), sourceKind: draft.sourceKind, ...(draft.sourceRef.trim() ? { sourceRef: draft.sourceRef.trim() } : {}), ...(draft.note.trim() ? { note: draft.note.trim() } : {}) }),
      });
      if (!ws.life.alive()) return;
      const item = readUnresolved(res.item);
      draft = { ...draft, label: "", value: "", sourceRef: "", note: "" };
      buildForm();
      form.querySelector("#fx-unr-form-result")?.replaceChildren(notice("ok", `Recorded as unresolved${item ? ` (${item.field === "other" ? item.label : fieldLabel(item.field)})` : ""}. It is NOT an approved fact and is not used for content or sites.`));
      await load();
      await ws.refreshFacts(); // the counter on the header
    } catch (e) {
      if (!ws.life.alive()) return;
      form.querySelector("#fx-unr-form-result")?.replaceChildren(problemNotice(describeProblem(e, "Not recorded")));
    }
  }

  // ---- one item --------------------------------------------------------------------------------------------------------------

  function itemBlock(it: UnresolvedItem): HTMLElement {
    const open = it.status === "unresolved";
    const subject = it.field === "other" ? it.label ?? "Other" : fieldLabel(it.field);
    const frame = h(
      "li",
      { class: `fx-unr-item${open ? "" : " fx-unr-item--closed"}`, dataset: { unresolvedId: it.id, status: it.status, field: it.field, approved: "false" } },
      h(
        "div",
        { class: "fx-unr-head" },
        h("strong", null, subject),
        open ? pill("UNRESOLVED: not approved", "warn") : it.status === "promoted" ? pill("Promoted to an approved fact", "ok") : pill("Rejected", "neutral"),
        it.field === "other" ? h("span", { class: "tag" }, "note only") : null,
      ),
      h("div", { class: "fx-unr-value" }, h("span", { class: "fx-unr-label" }, open ? "Proposed (unverified): " : "Was proposed: "), h("span", { class: "fx-unr-text" }, it.proposedValue)),
      h("div", { class: "muted fx-small" }, `Source: ${SOURCE_KIND_LABELS[it.sourceKind] ?? it.sourceKind}${it.sourceRef ? ` (${it.sourceRef})` : ""} · recorded by ${whoText(it.createdBy)}${it.createdAt ? ` · ${it.createdAt.text}` : ""}`),
      it.note ? h("div", { class: "muted fx-small" }, `Note: ${it.note}`) : null,
      !open ? h("div", { class: "muted fx-small" }, `${it.status === "promoted" ? "Promoted" : "Rejected"} by ${whoText(it.resolvedBy)}${it.resolvedAt ? ` · ${it.resolvedAt.text}` : ""}${it.resolutionNote ? ` · ${it.resolutionNote}` : ""}`) : null,
    );
    if (open) {
      if (promoting?.id === it.id) frame.append(promoteConfirm(it));
      else if (rejecting === it.id) frame.append(rejectForm(it));
      else
        frame.append(
          h(
            "div",
            { class: "btn-group fx-unr-actions" },
            it.field === "other" ? null : h("button", { type: "button", dataset: { action: "promote" }, "aria-label": `Promote ${subject} to an approved fact`, on: { click: () => void startPromote(it) } }, "Promote…"),
            h("button", { type: "button", class: "secondary", dataset: { action: "reject" }, "aria-label": `Reject ${subject}`, on: { click: () => void startReject(it) } }, "Reject…"),
          ),
          ...(it.field === "other" ? [h("div", { class: "muted fx-small" }, "A note cannot become an approved fact (it has no fact field). Reject it, or record the fact on the approved facts card.")] : []),
        );
    }
    return frame;
  }

  async function startPromote(it: UnresolvedItem) {
    // read the facts again first: the confirmation must show the CURRENT approved value and the revision the save will be guarded on
    await ws.refreshFacts();
    if (!ws.life.alive()) return;
    promoting = { id: it.id, stale: null, error: null };
    rejecting = null;
    flash = null;
    paint();
    out.querySelector<HTMLElement>(`[data-unresolved-id="${it.id}"] [data-confirm]`)?.focus();
  }
  async function startReject(it: UnresolvedItem) {
    rejecting = it.id;
    promoting = null;
    flash = null;
    paint();
    out.querySelector<HTMLElement>(`[data-unresolved-id="${it.id}"] textarea`)?.focus();
  }

  /** The explicit confirmation: says exactly what will become an approved fact, and what it replaces. */
  function promoteConfirm(it: UnresolvedItem): HTMLElement {
    const f = ws.facts;
    const preview = previewPromotion(it, f?.approved ?? {});
    let note = "";
    const noteInput = textInput("", { name: "note", onInput: (v) => void (note = v) });
    const confirm = busyButton({
      label: preview.unchanged ? "Promote anyway" : "Confirm: make this an approved fact",
      busyLabel: "Promoting…",
      id: `fx-promote-confirm-${it.id}`,
      onClick: async () => {
        if (!f) return;
        try {
          const res = await api<{ promoted?: boolean; diff?: Record<string, unknown> }>(`/v1/clients/${enc(ws.clientId)}/unresolved/${enc(it.id)}/resolve`, { method: "POST", body: JSON.stringify({ action: "promote", ifRevision: f.revision, ...(note.trim() ? { note: note.trim() } : {}) }) });
          if (!ws.life.alive()) return;
          promoting = null;
          flash = notice("ok", `Promoted: "${preview.fieldLabel}" is now an approved fact${res.promoted === false ? " (it was already that value)" : ""}. It was recorded in the history.`);
          await load();
          await ws.wrote();
        } catch (e) {
          if (!ws.life.alive()) return;
          const p = describeProblem(e, "Not promoted");
          promoting = { id: it.id, stale: p.kind === "stale" ? p : null, error: p.kind === "stale" ? null : p };
          if (p.kind === "resolved") await load();
          paint();
          out.querySelector<HTMLElement>(`[data-unresolved-id="${it.id}"] [data-confirm], [data-unresolved-id="${it.id}"] #fx-promote-reload`)?.focus();
        }
      },
    });
    confirm.dataset.confirm = "true";
    const box = h(
      "div",
      { class: "fx-promote", role: "group", "aria-label": `Confirm promoting ${preview.fieldLabel}`, dataset: { promoteFor: it.id } },
      h("strong", null, "Promote to an approved fact?"),
      !preview.promotable
        ? h("p", { class: "fx-promote__no" }, preview.reason ?? "This cannot be promoted.")
        : h(
            "div",
            null,
            h("p", null, "If you confirm, the approved fact ", h("strong", null, preview.fieldLabel), " will change:"),
            h("dl", { class: "deflist fx-promote__diff" }, h("dt", null, "Now (approved)"), h("dd", { dataset: { promote: "before" } }, preview.before), h("dt", null, "Becomes (approved)"), h("dd", { dataset: { promote: "after" } }, preview.after)),
            preview.unchanged ? h("p", { class: "muted" }, "This value already equals the approved one: promoting changes nothing, but closes this item.") : null,
            h("p", { class: "muted fx-small" }, `Recorded in the history as a change by the dashboard. The server checks the value like any other fact edit and refuses it if it is not valid. Guarded by revision ${f?.revision ?? "?"}: if the facts changed since you loaded them, nothing is saved.`),
            labelled("Note (optional)", noteInput),
            ...(promoting?.stale ? [h("div", { class: "fx-conflict", role: "alert" }, h("strong", null, "Conflict, reload"), h("p", null, promoting.stale.message), busyButton({ label: "Reload and review again", busyLabel: "Reloading…", id: "fx-promote-reload", onClick: async () => { await ws.refreshFacts(); if (ws.life.alive()) { promoting = { id: it.id, stale: null, error: null }; paint(); } } }))] : []),
            ...(promoting?.error ? [problemNotice(promoting.error)] : []),
          ),
      h("div", { class: "btn-group" }, preview.promotable && !promoting?.stale ? confirm : null, h("button", { type: "button", class: "secondary", on: { click: () => { promoting = null; paint(); } } }, "Cancel")),
    );
    return box;
  }

  function rejectForm(it: UnresolvedItem): HTMLElement {
    let note = "";
    const noteInput = textArea("", { name: "note", rows: 2, onInput: (v) => void (note = v) });
    const err = h("div", { "aria-live": "polite" });
    const go = busyButton({
      label: "Reject this item",
      busyLabel: "Rejecting…",
      variant: "danger",
      id: `fx-reject-confirm-${it.id}`,
      onClick: async () => {
        try {
          await api(`/v1/clients/${enc(ws.clientId)}/unresolved/${enc(it.id)}/resolve`, { method: "POST", body: JSON.stringify({ action: "reject", ...(note.trim() ? { note: note.trim() } : {}) }) });
          if (!ws.life.alive()) return;
          rejecting = null;
          flash = notice("ok", "Rejected. It stays on record as rejected and never became an approved fact.");
          await load();
          await ws.refreshFacts();
        } catch (e) {
          if (!ws.life.alive()) return;
          err.replaceChildren(problemNotice(describeProblem(e, "Not rejected")));
        }
      },
    });
    return h("div", { class: "fx-promote", role: "group", "aria-label": "Reject unresolved information" }, h("strong", null, "Reject this item?"), h("p", { class: "muted" }, "Nothing in the approved facts changes. The item is kept as a closed record."), labelled("Why (optional)", noteInput), err, h("div", { class: "btn-group" }, go, h("button", { type: "button", class: "secondary", on: { click: () => { rejecting = null; paint(); } } }, "Cancel")));
  }

  // The record form is placed once and never moved: a repaint of the lists must not take the keyboard away from someone typing in it.
  const bannerHost = h("div");
  const flashHost = h("div", { "aria-live": "polite" });
  const listHost = h("div");
  body.append(bannerHost, flashHost, h("h3", null, "Record something unconfirmed"), form, listHost);

  function paint() {
    const open = items?.filter((i) => i.status === "unresolved") ?? [];
    const closed = items?.filter((i) => i.status !== "unresolved") ?? [];
    const f = ws.facts;
    bannerHost.replaceChildren(
      h(
        "div",
        { class: "fx-unr-banner", role: "note" },
        h("strong", null, "NOT APPROVED. "),
        "Everything on this card is unconfirmed. It is stored apart from the approved facts and is never used for content or websites unless you promote it. ",
        f ? `${f.unresolvedOpen} open.` : "",
      ),
    );
    flashHost.replaceChildren(...(flash ? [flash] : []));
    listHost.replaceChildren(
      h("h3", null, `Open items (${open.length})`),
      loadError
        ? h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void load() } }, "Try again"))
        : items === null
          ? h("p", { class: "muted" }, "Loading…")
          : open.length === 0
            ? h("p", { class: "muted" }, "Nothing unresolved.")
            : h("ul", { class: "fx-unr-list", "aria-label": "Open unresolved information" }, ...open.map(itemBlock)),
      ...(closed.length
        ? [h(
            "div",
            null,
            h("button", { type: "button", class: "secondary", id: "fx-unr-toggle-closed", "aria-expanded": String(showClosed), on: { click: () => { showClosed = !showClosed; paint(); } } }, showClosed ? "Hide closed items" : `Show closed items (${closed.length})`),
            showClosed ? h("ul", { class: "fx-unr-list", "aria-label": "Closed unresolved information" }, ...closed.map(itemBlock)) : null,
          )]
        : []),
    );
  }

  buildForm();
  ws.onFacts(() => {
    // revision / counter changed: refresh text only when a confirmation is not open (it must keep what the person is reading)
    if (!promoting) paint();
  });
  void load();
  paint();
  return out;
}

export { showValue };
